/* ==========================================================================
   Landing Page
   Task F19: Marketing/landing page at /
   Task F33: Added pricing section, changed CTAs to Contact Sales
   ========================================================================== */

import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LogoutButton } from '@/components/ui/LogoutButton'
import { MarketingLayout } from '@/components/layout/MarketingLayout'
import { PricingSection } from '@/components/billing/PricingSection'
import { Send, Shield, Zap, Settings } from 'lucide-react'
import { getAdminToken, isAdminTokenExpired, getPharmacy, adminLogout } from '@/api/admin'
import { decodeJwtPayload, ADMIN_PHARMACY_SLUG_KEY } from '@/utils/portalSlug'
import { safeGetItem, safeRemoveItem } from '@/utils/safeStorage'

/* --------------------------------------------------------------------------
   Feature Card Component
   -------------------------------------------------------------------------- */

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <Card className="p-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-soft-lavender flex items-center justify-center">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-charcoal mb-1">{title}</h3>
          <p className="text-sm text-charcoal/70">{description}</p>
        </div>
      </div>
    </Card>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Landing() {
  const [pharmacySlug, setPharmacySlug] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function checkAdminAuth() {
      // Admin sessions are stored per portal slug (PORTAL-174). This page has no
      // slug in its URL, so it uses the last verified admin portal as the hint
      // for which session to show.
      const storedSlug = safeGetItem(ADMIN_PHARMACY_SLUG_KEY)
      if (!storedSlug) {
        setIsLoading(false)
        return
      }

      const token = getAdminToken(storedSlug)

      if (!token || isAdminTokenExpired(storedSlug)) {
        setIsLoading(false)
        return
      }

      const payload = decodeJwtPayload(token)
      if (!payload) {
        setIsLoading(false)
        return
      }

      try {
        await getPharmacy(storedSlug)
        setPharmacySlug(storedSlug)
      } catch {
        safeRemoveItem(ADMIN_PHARMACY_SLUG_KEY)
      }

      setIsLoading(false)
    }

    checkAdminAuth()
  }, [])

  const handleLogout = async () => {
    // Logs out (and clears the hint for) the portal shown, leaving any other
    // portal's admin session in this browser intact.
    await adminLogout(pharmacySlug)
    setPharmacySlug(null)
  }

  const nav = (
    <>
      <a
        href="#pricing"
        className="text-sm text-charcoal hover:text-deep-purple transition-colors"
      >
        Pricing
      </a>

      {isLoading ? (
        <div className="h-8 w-16 animate-pulse bg-gray-200 rounded" />
      ) : pharmacySlug ? (
        <>
          <Link to={`/rx/${pharmacySlug}/admin/dashboard`}>
            <Button variant="secondary" size="sm">
              <Settings className="h-4 w-4 mr-2" aria-hidden="true" />
              Portal Dashboard
            </Button>
          </Link>
          <LogoutButton onClick={handleLogout} />
        </>
      ) : (
        <Link to="/admin/login">
          <Button variant="primary" size="sm">
            Login
          </Button>
        </Link>
      )}
    </>
  )

  return (
    <MarketingLayout nav={nav}>
      {/* Hero Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-6">
              Streamline Prescription Requests for Compounding Pharmacies
            </h1>
            <p className="text-lg sm:text-xl text-charcoal/70 mb-8">
              A secure, white-labeled portal that connects healthcare providers
              with compounding pharmacies. Submit prescription requests in
              seconds with structured digital forms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact-sales">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  Contact Sales
                </Button>
              </Link>
              <a href="#pricing">
                <Button
                  variant="secondary"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  View Pricing
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-charcoal mb-4">
              Why Choose TJM Provider Portal?
            </h2>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              Built specifically for compounding pharmacies and their provider
              networks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Zap className="h-6 w-6 text-deep-purple" />}
              title="Fast Submissions"
              description="Pre-configured product catalogs mean providers can submit prescriptions in under a minute."
            />
            <FeatureCard
              icon={<Shield className="h-6 w-6 text-deep-purple" />}
              title="HIPAA Compliant"
              description="All data encrypted in transit and at rest. Built with healthcare security standards."
            />
            <FeatureCard
              icon={<Send className="h-6 w-6 text-deep-purple" />}
              title="Automated Faxing"
              description="Structured fax documents ready for automated processing. No manual data entry."
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-charcoal mb-4">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-deep-purple text-white flex items-center justify-center mx-auto mb-4 font-semibold">
                1
              </div>
              <h3 className="font-semibold text-charcoal mb-2">
                Pharmacy Setup
              </h3>
              <p className="text-sm text-charcoal/70">
                Create your branded portal and configure your product catalog
                with pre-set prescription details.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-deep-purple text-white flex items-center justify-center mx-auto mb-4 font-semibold">
                2
              </div>
              <h3 className="font-semibold text-charcoal mb-2">
                Invite Providers
              </h3>
              <p className="text-sm text-charcoal/70">
                Send invitations to your provider network. They set up their
                profile once and they're ready.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-deep-purple text-white flex items-center justify-center mx-auto mb-4 font-semibold">
                3
              </div>
              <h3 className="font-semibold text-charcoal mb-2">
                Submit Prescriptions
              </h3>
              <p className="text-sm text-charcoal/70">
                Providers select patient and product, submit, and the pharmacy
                receives a structured fax automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* CTA Section */}
      <section className="py-16 bg-deep-purple">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-4">
            Ready to Simplify Your Prescription Workflow?
          </h2>
          <p className="text-white/80 mb-8">
            Schedule a call with our team to get started with a plan that fits
            your pharmacy.
          </p>
          <Link to="/contact-sales">
            <Button
              variant="secondary"
              size="lg"
              className="bg-white text-deep-purple hover:bg-white/90"
            >
              Contact Sales
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  )
}

export default Landing
